import React, { useState, useEffect, useRef } from "react";
import {
  Grid,
  Switch,
  Select,
  Button,
  Dialog,
  MenuItem,
  Checkbox,
  TextField,
  InputLabel,
  FormControl,
  DialogTitle,
  Autocomplete,
  DialogContent,
  DialogActions,
  CircularProgress,
  DialogContentText,
} from "@mui/material";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import promoCodeAxios from "../../../utils/promoCodeAxiosService";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const AddCampaigns = () => {
  const agGridRef = useRef<AgGridReact | null>(null);
  const [countryListN, setCountryListN] = useState<any[]>([]);
  const [campaignName, setCampaignName] = useState("");
  const [operator, setOperator] = useState<any>(0);
  const [rowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // New Filter States
  const [endDate, setEndDate] = useState<any>("");
  const [startDate, setStartDate] = useState<any>("");
  const [destinationCountries, setDestinationCountries] = useState<any[]>([]);
  const [originationCountries, setOriginationCountries] = useState<any[]>([]);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [campaignData, setCampaignData] = useState<any>(null);
  const [activedata, setActiveData] = useState<any>(null);
  const [Open, setOpen] = useState<boolean>(false);
  const [actionRequest, setActionRequest] = useState<Boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editCampaignId, setEditCampaignId] = useState<any>(null);

  useEffect(() => {
    if (campaignData === null) getCampaignData();
  }, [campaignData]);

  useEffect(() => {
    loadCountries();
    getProducts();
  }, []);

  const today = dayjs();

  const loadCountries = async () => {
    await fetch("/data/thacountry.json")
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json: any) => {
        setCountryListN(json.thacountries);
      });
  };

  const getProducts = async () => {
    toast.dismiss();
    try {
      const response = await promoCodeAxios.get(`/ProductCode/GetAll`);

      const responseData = response?.data.payload || [];
      setRowData(responseData);
    } catch (error) {
      console.error(error);
    }
  };
  const getCampaignData = async () => {
    try {
      const response = await promoCodeAxios.get(`/Campaign/GetAll`);

      const responseData = response?.data.payload || [];
      setCampaignData(responseData);
    } catch (error) {
      console.error(error);
    }
  };

  const validateFormFields = (): boolean => {
    toast.dismiss();
    if (
      !campaignName ||
      !operator?.productCodeId ||
      originationCountries.length === 0 ||
      destinationCountries.length === 0 ||
      !startDate ||
      !endDate
    ) {
      toast.error("Please fill all the fields");
      return false;
    }

    if (startDate >= endDate) {
      toast.error("Start date must be earlier than end date");
      return false;
    }

    return true;
  };

  const AddCampaignData = async () => {
    if (!validateFormFields()) {
      return;
    }

    setIsLoading(true);

    const payload: any = {
      name: campaignName,
      originations: originationCountries?.map((c) => c.isoCode3),
      destinations: destinationCountries?.map((c) => c.isoCode3),
      productCode: operator?.productCode,
      startDate: startDate,
      endDate: endDate,
      isActive: isEnabled,
    };

    if (isEditMode) {
      payload.campaignId = editCampaignId;
    }

    try {
      const response = await promoCodeAxios.post(`/Campaign/Upsert`, payload);

      toast.success(response.data?.payload);
      setIsLoading(false);
      setCampaignData(null);
      clearForm();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.errors || "Something went wrong";
      toast.error(errorMessage);
      setIsLoading(false);
    }
  };

  const handleEditCampaign = (campaign: any) => {
    setIsEditMode(true);
    setEditCampaignId(campaign.campaignId);
    setCampaignName(campaign.name);
    setOperator(
      rowData?.find((item: any) => item.productCode === campaign.productCode)
    );
    setStartDate(campaign.startDate);
    setEndDate(campaign.endDate);
    setIsEnabled(campaign.isActive);

    // Populate origination countries
    if (campaign.origination && campaign.origination.length > 0) {
      const originCountries = countryListN.filter((country) =>
        campaign.origination.includes(country.isoCode3)
      );
      setOriginationCountries(originCountries);
    }

    // Populate destination countries
    if (campaign.destination && campaign.destination.length > 0) {
      const destCountries = countryListN.filter((country) =>
        campaign.destination.includes(country.isoCode3)
      );
      setDestinationCountries(destCountries);
    }

    // Scroll to top to show form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearForm = () => {
    setIsEditMode(false);
    setEditCampaignId(null);
    setCampaignName("");
    setOperator(0);
    setStartDate("");
    setEndDate("");
    setOriginationCountries([]);
    setDestinationCountries([]);
    setIsEnabled(true);
  };

  const ActionsCellRenderer: React.FC<{ data: any }> = ({ data }) => {
    return (
      <DriveFileRenameOutlineOutlinedIcon
        onClick={() => handleEditCampaign(data)}
        style={{ width: "90px", height: "32px", cursor: "pointer" }}
      ></DriveFileRenameOutlineOutlinedIcon>
    );
  };
  const CellRenderer: React.FC<{ data: any }> = ({ data }) => {
    return (
      <DeleteIcon
        onClick={() => {
          setOpen(true);
          setActiveData(data);
        }}
        style={{ cursor: "pointer" }}
      ></DeleteIcon>
    );
  };
  const columnDefs: ColDef[] = [
    {
      field: "name",
      headerName: "Name",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "productCode",
      headerName: "ProductCode",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "origination",
      headerName: "Origination",
      valueFormatter: (p) => p.value?.join(", "),
      resizable: true,
    },
    {
      field: "destination",
      headerName: "Destination",
      valueFormatter: (p) => p.value?.join(", "),
      resizable: true,
    },
    {
      field: "startDate",
      headerName: "StartDate",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "endDate",
      headerName: "EndDate",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "isActive",
      headerName: "IsActive",
      filter: true,
      sortable: true,
      resizable: true,
      cellDataType: "string",
    },
    { cellRenderer: ActionsCellRenderer },
    { cellRenderer: CellRenderer },
  ];
  const onGridReady = (params: { api: { sizeColumnsToFit: () => void } }) => {
    params.api.sizeColumnsToFit();
  };

  const deleteCampaign = async (Id: any) => {
    toast.dismiss();
    setActionRequest(true);
    try {
      const res = await promoCodeAxios.delete(`/Campaign/Remove`, {
        data: { campaignId: Id },
      });

      if (res.data?.payload) {
        toast.success(res.data.payload);
        setCampaignData(null);
        clearForm();
      } else {
        toast.error("Failed to delete campaign");
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.errors;
      toast.error(errorMessage);
      setOpen(false);
      setActionRequest(false);
    } finally {
      setOpen(false);
      setActionRequest(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div className="boxShadow">
        <h2 className="sectionTitle">
          {isEditMode ? "Edit Campaign" : "Add Campaign"}
        </h2>
        <div className="boxInner">
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <FormControl style={{ width: "100%" }}>
                <InputLabel style={{ background: "#fff", padding: "0 5px" }}>
                  Product
                </InputLabel>
                <Select
                  className="formControl"
                  autoWidth
                  value={operator?.productCodeId || "0"}
                  onChange={(e: any) => {
                    const operator = rowData?.find(
                      (item: any) => item.productCodeId === e.target.value
                    );
                    setOperator(operator);
                  }}
                >
                  <MenuItem value="0">Choose Type</MenuItem>
                  {rowData.length > 0 ? (
                    rowData?.map((item: any, index: any) => (
                      <MenuItem key={index} value={item.productCodeId}>
                        {item.productCode}
                      </MenuItem>
                    ))
                  ) : (
                    <div
                      className="circular-loader"
                      style={{ marginLeft: "0" }}
                    >
                      <CircularProgress size={40} />
                    </div>
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <fieldset>
                <legend>Data:</legend>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      fullWidth
                      label="Campaign Name"
                      value={campaignName}
                      onChange={(e) => setCampaignName(e.target.value)}
                      inputProps={{ maxLength: 40 }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      multiple
                      id="origination-countries"
                      options={countryListN || []}
                      disableCloseOnSelect
                      value={originationCountries || []}
                      onChange={(event, newValue) => {
                        setOriginationCountries(newValue);
                      }}
                      getOptionLabel={(option) => option.countryName}
                      isOptionEqualToValue={(option, value) =>
                        option.isoCode3 === value.isoCode3
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.countryName}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Origination Countries" />
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Autocomplete
                      multiple
                      id="destination-countries"
                      options={countryListN || []}
                      disableCloseOnSelect
                      value={destinationCountries || []}
                      onChange={(event, newValue) => {
                        setDestinationCountries(newValue);
                      }}
                      getOptionLabel={(option) => option.countryName}
                      isOptionEqualToValue={(option, value) =>
                        option.isoCode3 === value.isoCode3
                      }
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8 }}
                            checked={selected}
                          />
                          {option.countryName}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label="Destination Countries" />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Start Date"
                        value={startDate ? dayjs(startDate) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const formatted = newValue.format("YYYY-MM-DD");
                            setStartDate(formatted);
                            if (endDate && formatted > endDate) {
                              setEndDate(formatted);
                            }
                          } else {
                            setStartDate("");
                          }
                        }}
                        maxDate={endDate ? dayjs(endDate) : undefined}
                        slotProps={{
                          actionBar: {
                            actions: ["clear"],
                          },
                          textField: {
                            fullWidth: true,
                          },
                          field: {
                            readOnly: true,
                            onKeyDown: (e: React.KeyboardEvent) =>
                              e.preventDefault(),
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid item xs={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="End Date"
                        value={endDate ? dayjs(endDate) : null}
                        onChange={(newValue) => {
                          if (newValue) {
                            const formatted = newValue.format("YYYY-MM-DD");
                            setEndDate(formatted);
                          } else {
                            setEndDate("");
                          }
                        }}
                        minDate={startDate ? dayjs(startDate) : undefined}
                        slotProps={{
                          actionBar: {
                            actions: ["clear"],
                          },
                          textField: {
                            fullWidth: true,
                          },
                          field: {
                            readOnly: true,
                            onKeyDown: (e: React.KeyboardEvent) =>
                              e.preventDefault(),
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </Grid>

                  <Grid
                    item
                    xs={4}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>Campaign:</span>
                    <span> Disable</span>
                    <Switch
                      checked={isEnabled}
                      onChange={(e) => setIsEnabled(e.target.checked)}
                    />
                    <span>Enable</span>
                  </Grid>
                </Grid>
                <Grid
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                  }}
                >
                  <Button
                    variant="contained"
                    className="dButton dButton2"
                    onClick={() => {
                      AddCampaignData();
                    }}
                    disabled={isLoading}
                    sx={{
                      marginTop: "15px !important",
                      width: "280px !important",
                    }}
                  >
                    {isLoading ? <CircularProgress size={30} /> : "Submit"}
                  </Button>
                  {isEditMode && (
                    <Button
                      variant="contained"
                      className="dButton dButton2 dbuttonSec"
                      onClick={() => {
                        clearForm();
                      }}
                      disabled={isLoading}
                      sx={{
                        marginTop: "15px !important",
                        width: "280px !important",
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </Grid>
              </fieldset>
            </Grid>
          </Grid>
        </div>
      </div>
      {!campaignData && (
        <div className="dummy-conatiner">
          <CircularProgress size={40} />
        </div>
      )}
      {campaignData && (
        <div className="ag-theme-alpine">
          <AgGridReact
            ref={agGridRef}
            paginationPageSize={8}
            pagination={true}
            domLayout="autoHeight"
            rowData={campaignData}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            enableCellTextSelection={true}
          ></AgGridReact>
        </div>
      )}

      {activedata && (
        <Dialog
          open={Open}
          onClose={() => setOpen(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            {"Delete Confirmation"}
          </DialogTitle>

          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              Would you really like to Delete this Campaign?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              className="dButton dButton2 dbuttonSec"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <div style={{ width: "30px" }}></div>
            {!actionRequest && (
              <Button
                variant="contained"
                className="dButton dButton2"
                onClick={() => {
                  deleteCampaign(activedata.campaignId);
                }}
                autoFocus
              >
                Delete
              </Button>
            )}
            {actionRequest && (
              <Button className="dButton dButton2" variant="contained">
                <CircularProgress color="inherit" size={30} />
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}
    </motion.div>
  );
};

export default AddCampaigns;
